import { z } from 'zod'

const positiveIntegerSchema = z
	.number({ message: 'errors:integer_error' })
	.int({ message: 'errors:integer_error' })
	.min(0, { message: 'errors:positive_number_error' })

const BaseListingSchema = z.object({
	listingName: z.string().min(1, 'errors:field_required'),
	hostEmail: z.email('errors:email_valid'),
	walkthroughContactEmail: z
		.union([z.literal(''), z.email('errors:email_valid')])
		.optional(),
	geo: z.string().min(1, 'errors:field_required'),
	bedrooms: positiveIntegerSchema,
	beds: positiveIntegerSchema,
	bathrooms: positiveIntegerSchema,
	guestCount: positiveIntegerSchema,
	listingInventory: z
		.enum(
			[
				'standard_mp_conversion',
				'standard_nal',
				'branded_residence_mp_conversion',
				'branded_residence_nal',
				''
			],
			{
				error: () => 'errors:field_required'
			}
		)
		.refine((val) => val !== '', {
			message: 'errors:field_required'
		})
})

export const AirbnbListingSchema = BaseListingSchema.extend({
	listingUrl: z.url('errors:invalid_url'),
	listingId: z.string().min(1, 'errors:field_required')
})
export type AirbnbListingSchema = z.infer<typeof AirbnbListingSchema>

export const MIN_LUXE_PHOTOS = 10
const PhotosSchema = z
	.object({
		photoSubmissionType: z
			.enum(['', 'url', 'device'])
			.refine((val) => val !== '', {
				message: 'errors:field_required'
			}),
		photoRepositoryUrl: z.string().optional(),
		galleryId: z.string().optional(),
		photos: z.array(
			z.object({
				galleryId: z.string(),
				presignedUrl: z.string(),
				imageId: z.string(),
				imageUrl: z.string(),
				uploading: z.boolean(),
				failed: z.boolean().optional()
			})
		)
	})
	.superRefine((data, ctx) => {
		if (data.photoSubmissionType === 'url') {
			const urlResult = z
				.url('errors:invalid_url')
				.safeParse(data.photoRepositoryUrl)
			if (!urlResult.success) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'errors:invalid_url',
					path: ['photoRepositoryUrl']
				})
			}
		}
		if (data.photoSubmissionType === 'device') {
			if (data.photos.length < MIN_LUXE_PHOTOS) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'errors:min_photos_required',
					path: ['photos']
				})
			}

			if (!data.galleryId) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'errors:field_required',
					path: ['galleryId']
				})
			}
		}
	})

export const OtherListingSchema = BaseListingSchema.extend({
	listingUrl: z.url('errors:invalid_url').optional()
}).and(PhotosSchema)

export type OtherListingSchema = z.infer<typeof OtherListingSchema>
